import { CalendarDate, type DateFields } from "@internationalized/date";
import { OpenMode, useLocalStorageForm } from "@onesolution/components";
import type { OperationFilters } from "@onesolution/utils/makeOperationFilters";
import { useEffect, useMemo } from "react";
import { useDebounceValue } from "usehooks-ts";

const bufferDate = new CalendarDate(2020, 1, 1);
export function makeDefaultOpFilters(status?: string[]) {
	return {
		name: "",
		dateRange: undefined,
		status: status || [],
		services: [],
		entities: [],
		campaigns: "",
		marketingPlans: "",
		email: "",
	} as OperationFilters;
}

export function useOperationFilters(key: string, additionalFilters?: object) {
	const form = useLocalStorageForm<OperationFilters>({
		mode: OpenMode.edition,
		localStorageKey: `@onevision-operations-filters-${key}`,
		defaultValues: {
			...makeDefaultOpFilters(),
			...(additionalFilters || {}),
		},
		formatOnLoad: (values: {
			dateRange?: { start: string; end: string };
		}) => {
			if (values.dateRange?.start && values.dateRange?.end) {
				Object.assign(values, {
					dateRange: {
						start: bufferDate.set(values.dateRange.start as DateFields).copy(),
						end: bufferDate.set(values.dateRange.end as DateFields).copy(),
					},
				});
			}

			return values;
		},
		onSuccess: () => null,
		onSubmitMutation: () => null,
	});
	const [debouncedFilters, updateDebouncedFilters] = useDebounceValue(
		form.state.values,
		500,
	);
	const formattedFilters = useMemo(
		() => ({
			...debouncedFilters,
			dateRange: {
				start: debouncedFilters.dateRange?.start?.toString(),
				end: debouncedFilters.dateRange?.end?.toString(),
			},
		}),
		[debouncedFilters],
	);

	useEffect(() => {
		updateDebouncedFilters(form.state.values);
	}, [form.state.values, updateDebouncedFilters]);

	return { form, filters: formattedFilters };
}
